const MILLISECONDS_PER_DAY = 86400000;

/**
 * The practice recall list, read as work rather than as rows (KIN-1858).
 *
 * The backend already sorts soonest-due-first and already decides what belongs here — a
 * dose the school programme delivers outside the funded ages is excluded rather than shown
 * as this practice's work. Nothing below re-judges any of that.
 */

/** Two names into one, without leaving a stray space when one of them is missing. */
export const recallPatientName = ({ firstName, lastName } = {}) =>
  [firstName, lastName].filter(Boolean).join(" ");

/**
 * Whether the earliest outstanding dose is already past its due date.
 *
 * The backend has no OVERDUE status — DUE means "you can give this now", whether it became
 * giveable today or a year ago — so the distinction is made here, the same way the patient
 * schedule makes it.
 */
export const isRecallOverdue = (recall, today = new Date()) => {
  if (!recall?.earliestDueOn) return false;

  const due = new Date(`${recall.earliestDueOn}T00:00:00`);
  const midnightToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return due.getTime() < midnightToday.getTime();
};

export const daysOverdue = (recall, today = new Date()) => {
  if (!isRecallOverdue(recall, today)) return 0;

  const due = new Date(`${recall.earliestDueOn}T00:00:00`);
  const midnightToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return Math.round(
    (midnightToday.getTime() - due.getTime()) / MILLISECONDS_PER_DAY
  );
};

/**
 * Split the list into confirmed work and rows that are only candidates.
 *
 * No immunisation history migrates into this system, so a patient whose record does not
 * cover their whole history looks due for everything. Those rows are shown — they are
 * often the ones most worth chasing — but never mixed in with the confirmed work, because
 * calling a parent to say their child is behind, when the practice simply has no record,
 * is the one outcome this list must not produce.
 */
export const splitRecallByCertainty = (recallList) => {
  const rows = recallList || [];

  return {
    confirmed: rows.filter((row) => !row.historyMayBeIncomplete),
    toConfirm: rows.filter((row) => row.historyMayBeIncomplete),
  };
};

/** The outstanding doses as one readable line: "6 in 1 dose 2, MenB dose 2". */
export const summariseOutstanding = (recall, formatCourseName) =>
  (recall?.outstanding || [])
    .map((slot) => `${formatCourseName(slot)} dose ${slot.doseNumber}`)
    .join(", ");
